/** @format */

import React, { FC, useRef, useState } from 'react'
import { MonitorCrumb } from 'src/components'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router'
import { useAsync } from 'react-use'
import useNavRegion from 'src/hooks/useNavRegion'
import {
  _$,
  Card,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Button,
  Message,
  Datepicker
} from '@king-design/react'
import { getPromethuesInstance } from 'src/api/ApiPromethuesInstance'
import {
  getPrometheusAlertSilences,
  createPrometheusAlertSilence,
  updatePrometheusAlertSilence
} from 'src/api/ApiPrometheusAlarm'
import { initSilenceFormValues, DenoiseSilenceEditProps } from '../constants'
import MatchRule from '../components/MatchRule'
import { getTimeDiffSimple } from 'src/utils/date'
import dayjs from 'dayjs'
function useQuery() {
  return new URLSearchParams(useLocation().search)
}
const DenoiseInhibitionEdit: FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const slienceID = query.get('slienceID')
  const copyID = query.get('copyID')
  // 控制台region
  const region = useNavRegion()
  // form表单
  const formRef = useRef(null)
  const [formValue, setFormValue] = useState<DenoiseSilenceEditProps>(initSilenceFormValues)
  // 账户下实例
  const { value: instances } = useAsync(async () => {
    if (region) {
      const result = await getPromethuesInstance({
        SearchRegion: region
      })
      return (
        result?.InstancesSet?.map(item => {
          return { name: item.InstanceName, id: item.ControllerID }
        }) || []
      )
    } else {
      return []
    }
  }, [region])

  useAsync(async () => {
    if ((copyID || slienceID) && instances !== undefined) {
      const result = await getPrometheusAlertSilences({
        ID: parseInt(copyID || slienceID)
      })
      const rule = result?.Rules?.[0]
      if (rule) {
        setFormValue({
          Name: copyID ? `${rule.Name}_copy` : rule.Name,
          InstanceIDs: rule.BindInstances.map(item => item.InstanceID),
          Description: rule.Description,
          ParallelMatchers: rule.ParallelMatchers,
          Time: [rule.StartsAt * 1000, rule.EndsAt * 1000],
          Duration: rule.Duration
        })
      }
    }
  }, [slienceID, copyID, instances])

  // 提交
  const formSubmit = async () => {
    const validate = await formRef?.current?.validate()
    try {
      if (validate) {
        const { Name, InstanceIDs, Description, ParallelMatchers, Time, Duration } = formValue
        const params = {
          Name,
          InstanceIDs,
          Description,
          ParallelMatchers,
          StartsAt: Time[0] / 1000,
          EndsAt: Time[1] / 1000,
          Duration
        }
        const result = slienceID
          ? await updatePrometheusAlertSilence({
              ID: parseInt(slienceID),
              ...params
            })
          : await createPrometheusAlertSilence({
              ...params
            })
        if (result?.code === 'Success') {
          Message.success(_$('操作成功'))
          history.goBack()
        }
      }
    } catch (e) {
      return false
    }
  }

  return (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: _$('Prometheus监控') },
          {
            title: _$('告警管理'),
            route: '/monitor/prometheusmonitor/alarm?tab=denoise&denoiseTab=inhibition'
          },
          { title: slienceID ? _$('编辑静默规则') : _$('新建静默规则') }
        ]}
      />
      <Card>
        <Form className="form_container" starOnRequired ref={formRef} labelWidth={100}>
          <FormItem
            label={_$('静默规则名称')}
            value={formValue.Name}
            rules={{ required: true, userName: true }}
          >
            <Input
              placeholder={_$('请输入静默规则名称')}
              value={formValue.Name}
              onChangeValue={value => {
                setFormValue({ ...formValue, Name: value })
              }}
            />
          </FormItem>
          <FormItem
            className="form_item_instance"
            label={_$('关联资源')}
            value={formValue.InstanceIDs}
            rules={{ required: true }}
          >
            <Select
              value={formValue.InstanceIDs}
              clearable
              filterable
              multiple
              container={() => document.getElementsByClassName('form_item_instance')[0]}
              onChangeValue={value => {
                setFormValue({ ...formValue, InstanceIDs: value })
              }}
            >
              {instances?.map(item => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label={_$('描述')} value={formValue.Description} rules={{ maxLength: 512 }}>
            <Input
              type="textarea"
              value={formValue.Description}
              rows={5}
              resize="none"
              onChangeValue={value => {
                setFormValue({ ...formValue, Description: value })
              }}
            />
          </FormItem>
          <FormItem
            label={_$('匹配规则')}
            value={formValue.ParallelMatchers}
            rules={{ required: true }}
          >
            <MatchRule
              rules={formValue.ParallelMatchers}
              onChange={value => {
                setFormValue({ ...formValue, ParallelMatchers: value })
              }}
            />
          </FormItem>
          <FormItem
            label={_$('静默时间')}
            value={formValue.Time}
            rules={{ required: true, endTimeValidate: true }}
          >
            <Datepicker
              width={330}
              format="YYYY-MM-DD HH:mm"
              range
              type="datetime"
              value={formValue.Time}
              clearable
              min={dayjs().subtract(30, 'day')}
              max={dayjs().add(30, 'day')}
              onChangeValue={date => {
                if (date?.[0] && date[1]) {
                  const startTime = dayjs(date[0]).valueOf()
                  const endTime = dayjs(date[1]).valueOf()
                  const diff = Math.abs(dayjs(date[0]).diff(dayjs(date[1]), 'second'))
                  if (diff > 30 * 24 * 60 * 60) {
                    Message.error(_$('开始时间与结束时间最大30天'))
                    const duration = getTimeDiffSimple(
                      startTime,
                      dayjs(date[0]).add(30, 'days').valueOf()
                    )
                    setFormValue({
                      ...formValue,
                      Time: [dayjs(date[0]).valueOf(), dayjs(date[0]).add(30, 'days').valueOf()],
                      Duration: duration
                    })
                  } else {
                    const duration = getTimeDiffSimple(startTime, endTime)
                    setFormValue({
                      ...formValue,
                      Time: [startTime, endTime],
                      Duration: duration
                    })
                  }
                }
              }}
            />
          </FormItem>
          <FormItem label={_$('持续时间')}>
            <p style={{ margin: 0, lineHeight: '32px' }}>{formValue.Duration || '-'} </p>
          </FormItem>
        </Form>
      </Card>
      <div className="bottom_action">
        <Button onClick={() => formSubmit()} type="primary">
          {_$('确认')}
        </Button>
        <Button
          onClick={() => {
            history.goBack()
          }}
          type="default"
        >
          {_$('取消')}
        </Button>
      </div>
    </div>
  )
}

export default styled(DenoiseInhibitionEdit)`
  padding: 0 16px;
  position: relative;
  padding-bottom: 80px;
  .k-card {
    padding: 16px 0;
    .k-card-body {
      height: calc(100vh - 228px);
      overflow-y: auto;
      padding-bottom: 0;
      padding-top: 0;
      padding-right: 0;
      .k-form-label {
        .ion-help-circled {
          margin-left: 4px;
        }
      }
      .k-datepicker {
        border-radius: 4px;
      }
    }
  }
  .bottom_action {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 64px;
    padding: 16px 0;
    background: #ffffff;
    padding-right: 16px;
    z-index: 98;
    border-radius: 6px 6px 0 0;
    button {
      font-size: 14px;
      margin-left: 8px;
    }
  }
`
